// S1 진입 — 통로 둘이 만나는 화면
//
// **세 단계의 순서가 이 화면의 존재 이유다.** 코드를 넣으면 그 room 의 CT 닉네임이
// 먼저 뜨고, 접속자는 맞는 방에 들어왔는지를 **게임 ID 를 넣기 전에** 확인한다. 두
// 단계를 한 화면에 붙이면 그 확인이 사라진다 — 코드가 틀렸다는 것을 입장에 실패한
// 뒤에야 알게 되고, 전투 직전에 그 왕복은 비싸다.
//
// **CT 갈래에는 room 을 만드는 버튼이 없다.** room 은 서버가 처음 기동할 때 만들고
// 앱에는 그 문이 없다. 관리 코드를 넣는 칸 하나가 CT 갈래의 전부다.

import { useCallback, useEffect, useRef, useState } from 'react'

import type { PresenceEntry } from '../../../contract/schema.ts'
import type { SessionGoneCause } from '../../../contract/ws.ts'
import { entry as entryStrings, type Lang } from '../../../i18n/index.ts'
import {
  call,
  clearSessionGoneNotice,
  CT_TOKEN_KEY,
  clearToken,
  readSessionGoneNotice,
  readToken,
  routes,
  useLiveState,
  VIEWER_GAME_ID_KEY,
  VIEWER_TOKEN_KEY,
  writeToken,
  clearCtGoneNotice,
} from '../../access/client.ts'
import { TopBar } from '../../shell/topbar.tsx'
import { useShellClock } from '../../shell/use-shell-clock.ts'
import { useLang } from '../../shell/use-lang.ts'
import { Icon, IconTile, Wordmark } from '../../ui/icons.tsx'

type Step = 'code' | 'confirm' | 'gameId' | 'joined' | 'ct'

/**
 * 화면 맨 위의 거절 줄. 서버가 준 문장이거나, **세션이 사라진 원인**이다.
 *
 * 원인을 문장이 아니라 원인 그대로 드는 이유는 언어다. 문장으로 박아 두면 사람이 KO/EN 을 바꿔도 그
 * 한 줄만 처음 언어로 남는다(적대 리뷰가 찾았다). 원인으로 들면 그릴 때마다 지금 언어로 푼다.
 * 칸을 고치면 거절 줄을 지우는 자리들이 `setError('')` 하나로 둘 다 거둔다.
 */
type EntryError = string | { gone: SessionGoneCause }

function goneMessage(t: ReturnType<typeof entryStrings>, cause: SessionGoneCause): string {
  if (cause === 'removed') return t.sessionGoneRemoved
  if (cause === 'kicked') return t.sessionGoneKicked
  return t.sessionGone
}

export function EntryScreen(): React.ReactElement {
  const clock = useShellClock()
  // **이 화면이 저장소를 고른 이유 그 자체다.** 언어를 세션에 두면 아직 입장하지 않은
  // 여기서 고를 수 없다. 그런데 2026-08-17 배선은 이 화면을 빠뜨려서, 근거가 된 화면만
  // 저장소를 읽지도 쓰지도 않았다 — 같은 날 대리 관측이 그것을 잡았다.
  const { lang, choose: setLang } = useLang()
  const t = entryStrings(lang)

  // 새로고침해도 돌아온다. 토큰이 남아 있으면 다시 입장하지 않는다 — 그것이 곧 세션
  // 승계이고, 새로고침이 접속 수를 늘리지 않는 이유다.
  const [sessionToken, setSessionToken] = useState<string | null>(() => readToken(VIEWER_TOKEN_KEY))
  // **주소에 `?ct` 가 있으면 관리 코드 칸부터 연다.** 구성 화면이 CT 에게 관리 코드를 넣으라고 하면서 보내는
  // 자리다. 연맹원 입장 칸으로 보내면 CT 링크를 한 번 더 찾아야 했다(2026-10-07 대리 관측).
  const [step, setStep] = useState<Step>(() => {
    if (new URLSearchParams(window.location.search).has('ct')) return 'ct'
    return readToken(VIEWER_TOKEN_KEY) === null ? 'code' : 'joined'
  })

  const [code, setCode] = useState('')
  const [adminCode, setAdminCode] = useState('')
  // 새로고침으로 돌아온 화면이 자기가 누구인지 알아야 한다. 토큰만 남기면 이름 자리가
  // 빈 채로 남고, 그것이 뒤 스텝의 「누구 것인지 항상 화면에 있다」를 곧바로 깨뜨린다.
  const [gameId, setGameId] = useState(() => readToken(VIEWER_GAME_ID_KEY) ?? '')
  const [ctNickname, setCtNickname] = useState('')
  // **입장 응답이 준 이름은 정본이 아니라 씨앗이다.** 명단 방송이 닿기 전 한 순간을 메울
  // 뿐이고, 그 뒤로는 아래에서 명단이 이긴다.
  const [joinedNickname, setJoinedNickname] = useState('')
  // **열람 화면이 세션을 잃고 보낸 탭이면 처음부터 그 이유를 말한다** (T4 개정 둘째). 지워지거나
  // 추방된 사람이 아무 말 없는 코드 칸만 보면 자기가 무엇을 잘못 눌렀는지부터 의심한다. 표지는
  // 한 번만 말하도록 아래 효과가 지운다.
  const [error, setError] = useState<EntryError>(() => {
    const cause = readSessionGoneNotice()
    return cause === null ? '' : { gone: cause }
  })
  useEffect(() => {
    clearSessionGoneNotice()
  }, [])
  const [busy, setBusy] = useState(false)

  const live = useLiveState(step === 'joined' ? sessionToken : null)

  // **이름을 붙들지 않고 파생시킨다.**
  //
  // 붙들었을 때 무엇이 깨지는가: CT 가 명부에서 이름을 고치면 이 화면 아래의 접속 명단은
  // 방송을 받아 1초 안에 새 이름이 되는데, 맨 위 큰 글씨는 입장 순간의 값에 얼어붙는다.
  // 그러면 한 사람의 **두 이름이 손가락 두 마디 거리에서 동시에** 뜬다. 이름이 둘이면
  // 없는 것과 같고, 「지금 보는 것이 누구 것인가」가 화면에 있다고 말할 수 없다.
  //
  // 고치기 전에는 「값이 비어 있을 때만 명단에서 되찾는」 효과가 이 자리에 있었다. 그것은
  // 새로고침으로 돌아온 화면의 빈 이름 자리를 메우려던 것인데, **한 번 채워지면 다시는 안
  // 읽는다**는 성질이 함께 딸려 왔다. 파생값으로 바꾸면 새로고침 복구와 이름 갱신이 같은
  // 한 줄이 된다 — 어긋남이 고쳐지는 것이 아니라 만들어질 수 없다.
  //
  // (2026-08-14, 공격 열람 스텝의 선결 조건이다. 그 화면이 이 이름을 카운트다운 바로 위에
  // 크게 올리는데 그 항목은 통과·불통이라, 낡은 채로 두면 축 하나가 통째로 0점이 된다.)
  const nickname = live.entries.find((row) => row.gameId === gameId)?.nickname ?? joinedNickname

  const checkCode = useCallback(async (): Promise<void> => {
    setBusy(true)
    setError('')
    const result = await call<{ ctNickname: string }>(routes.joinCheck, { body: { code } })
    setBusy(false)
    if (!result.ok) {
      setError(result.message)
      return
    }
    setCtNickname(result.data.ctNickname)
    setStep('confirm')
  }, [code])

  const join = useCallback(async (): Promise<void> => {
    setBusy(true)
    setError('')
    // 서버도 앞뒤 공백을 지우지만 여기서도 지운다. 저장해 둔 값이 명단 줄의 게임 ID 와 같은
    // 문자열이어야 아래에서 내 이름을 찾는다. 공백이 남으면 들어와 놓고 내 줄을 못 찾는다.
    const trimmed = gameId.trim()
    const result = await call<{ sessionToken: string; nickname: string }>(routes.join, {
      body: { code, gameId: trimmed },
    })
    setBusy(false)
    if (!result.ok) {
      setError(result.message)
      return
    }
    writeToken(VIEWER_TOKEN_KEY, result.data.sessionToken)
    writeToken(VIEWER_GAME_ID_KEY, trimmed)
    setGameId(trimmed)
    setSessionToken(result.data.sessionToken)
    setJoinedNickname(result.data.nickname)
    setStep('joined')
  }, [code, gameId])

  const claimCt = useCallback(async (): Promise<void> => {
    setBusy(true)
    setError('')
    const result = await call<{ ctToken: string }>(routes.ctClaim, { body: { adminCode } })
    setBusy(false)
    if (!result.ok) {
      setError(result.message)
      return
    }
    writeToken(CT_TOKEN_KEY, result.data.ctToken)
    // 끊긴 CT 화면의 표지는 다시 들어온 순간 지운다. 남기면 다음 끊김이 아닌데도 옛 이유를 말한다.
    clearCtGoneNotice()
    window.location.assign('/compose')
  }, [adminCode])

  const leave = useCallback((): void => {
    clearToken(VIEWER_TOKEN_KEY)
    clearToken(VIEWER_GAME_ID_KEY)
    setSessionToken(null)
    setStep('code')
    setCode('')
    setGameId('')
    // 씨앗도 함께 거둔다. 남겨 두면 다른 게임 ID 로 다시 들어온 첫 순간에 **앞사람의
    // 이름**이 뜬다 — 명단이 닿기 전까지의 그 한 순간이 이 화면에서 가장 나쁜 자리다.
    setJoinedNickname('')
  }, [])

  // **서버가 이 세션을 모르면 입장 상태를 거둔다.** 그러지 않으면 화면 맨 위가
  // 「입장했습니다」인데 이름 자리는 비고 명단은 「지금 값이 아닙니다」인, 스스로
  // 모순되는 상태가 남는다. 서버에 세션이 없다는 것은 추측이 아니라 서버가 답한
  // 사실이므로, 화면은 물러나 다시 들어올 길을 내주는 편이 맞다.
  //
  // **원인은 서버가 닫힘 이유로 말해 준 것만 말한다** (T4 개정 둘째). 명부에서 지웠는지 추방했는지는
  // 서버가 싣고, 그 밖(방이 다시 만들어짐, 붙어 보니 이미 없는 세션)은 화면이 구별할 근거가 없어
  // 「이 접속은 끝났습니다」로만 말한다.
  //
  // 저장된 토큰이 이 화면이 쥔 토큰과 같을 때만 지운다. 다른 탭이 그 사이 새로 받은 토큰을 지우지
  // 않으려는 것이다(`markSessionGone` 의 주석).
  //
  // **쥔 토큰을 의존성이 아니라 ref 로 읽는다.** 의존성에 넣으면 다시 입장해 새 토큰을 받는 렌더에서
  // 이 효과가 다시 도는데, 그때 `live.sessionGone` 은 아직 앞 토큰의 `true` 라 방금 받은 새 토큰을
  // 지운다. 이 효과는 서버의 답이 바뀔 때만 돌아야 한다.
  const sessionTokenRef = useRef(sessionToken)
  sessionTokenRef.current = sessionToken
  useEffect(() => {
    if (!live.sessionGone) return
    if (readToken(VIEWER_TOKEN_KEY) === sessionTokenRef.current) {
      clearToken(VIEWER_TOKEN_KEY)
      clearToken(VIEWER_GAME_ID_KEY)
    }
    setSessionToken(null)
    setJoinedNickname('')
    setStep('code')
    setError({ gone: live.goneCause ?? 'unknown' })
  }, [live.sessionGone, live.goneCause])

  return (
    <>
      <TopBar
        utcMillis={clock.utcMillis}
        skew={clock.skew}
        lang={lang}
        onLangChange={setLang}
      />

      <main className="col-narrow-card card mt-section font-sans [--card-py:var(--spacing-block)]" data-testid="entry-screen">
        <h1 className="panel-head brand-head cat-accent text-lg font-bold tracking-tight">
          <IconTile name="mark" tone="accent" size="lg" />
          <span>
            <Wordmark text={t.title} />
          </span>
        </h1>
        {/* 첫 화면 안내 문구는 정본 화면 원칙 절이 원문으로 확정한 것이다. 넣을 것과
            얻을 것을 한 문장에 담은 형태를 고른 이유가 거기 적혀 있다.

            **CT 갈래에서는 다른 문장이 온다.** 원문은 카운트다운을 **받는** 사람에게
            한 약속인데, CT 는 그것을 만드는 사람이다. 같은 문장을 이고 있으면 그 화면이
            누구를 위한 것인지가 흐려진다.

            **CT 확인 단계와 입장 완료 화면에는 안내 줄이 없다**(2026-10-12 사용자). 두 단계는 제목이 이미
            할 일을 말하는데, 첫 화면 문장 「입장 코드를 넣으면」이 남아 이미 코드를 넣은 사람에게 코드를
            넣으라고 했다(같은 날 대리 관측). 게임 ID 단계는 그 단계의 문장을 쓴다. */}
        {(step === 'code' || step === 'ct' || step === 'gameId') && (
          <p className="mt-line text-sm text-ink-dim" data-testid="entry-guide">
            {step === 'ct' ? t.ctGuide : step === 'gameId' ? t.gameIdGuide : t.guide}
          </p>
        )}

        {error !== '' && (
          <p
            role="alert"
            data-testid="entry-error"
            className="note note-alert mt-block text-sm"
          >
            {typeof error === 'string' ? error : goneMessage(t, error.gone)}
          </p>
        )}

        {step === 'code' && (
          <section className="mt-section" aria-label={t.codeLabel}>
            <label className="section-title caps block" htmlFor="join-code">
              {t.codeLabel}
            </label>
            <input
              id="join-code"
              data-testid="join-code-input"
              className="field mt-line font-mono text-base font-medium tracking-widest"
              placeholder={t.codePlaceholder}
              value={code}
              autoComplete="off"
              enterKeyHint="next"
              // Enter 와 폰 키보드의 이동 키는 아래 버튼과 같다. 없으면 그 키로 넘어가려는 사람이 아무 반응을 받지
              // 못했다(2026-10-07 대리 관측). 버튼이 꺼져 있을 때(빈 칸, 보내는 중)는 아무것도 하지 않는다.
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || busy || code.trim() === '') return
                event.preventDefault()
                void checkCode()
              }}
              onChange={(event) => {
                // 값을 고치면 앞 값에 대한 거절은 지운다. 9자를 고쳐 10자로 만든 뒤에도 「넣은 값은
                // 9자입니다」가 남으면 화면이 지금 칸에 대해 틀린 말을 한다(2026-10-01 대리 관측).
                setError('')
                setCode(event.target.value)
              }}
            />
            <button
              type="button"
              data-testid="join-code-submit"
              disabled={busy || code.trim() === ''}
              className="btn btn-primary mt-group w-full"
              onClick={() => void checkCode()}
            >
              {t.codeSubmit}
            </button>

            <button
              type="button"
              data-testid="ct-branch"
              className="btn btn-ghost mt-block w-full"
              onClick={() => {
                setStep('ct')
                setError('')
              }}
            >
              {t.ctBranch}
            </button>
          </section>
        )}

        {step === 'confirm' && (
          <section className="mt-section" aria-label={t.confirmTitle} data-testid="ct-confirm">
            <p className="text-sm text-ink-dim">{t.confirmTitle}</p>
            {/* 이 한 줄이 오입장을 잡는 장치 전부다. 게임 ID 칸보다 **먼저** 온다. */}
            <p data-testid="ct-nickname" className="mt-group text-2xl font-semibold">
              {ctNickname}
            </p>
            <div className="mt-block flex gap-2">
              <button
                type="button"
                className="btn btn-secondary flex-1"
                onClick={() => {
                  setStep('code')
                }}
              >
                {t.confirmBack}
              </button>
              <button
                type="button"
                data-testid="ct-confirm-next"
                className="btn btn-primary flex-1"
                onClick={() => {
                  setStep('gameId')
                }}
              >
                {t.confirmNext}
              </button>
            </div>
          </section>
        )}

        {step === 'gameId' && (
          <section className="mt-section" aria-label={t.gameIdLabel}>
            <label className="section-title caps block" htmlFor="game-id">
              {t.gameIdLabel}
            </label>
            <input
              id="game-id"
              data-testid="game-id-input"
              inputMode="numeric"
              className="field mt-line font-mono text-base font-medium"
              placeholder={t.gameIdPlaceholder}
              value={gameId}
              autoComplete="off"
              enterKeyHint="go"
              // Enter 와 폰 키보드의 이동 키는 아래 버튼과 같다. 없으면 그 키로 넘어가려는 사람이 아무 반응을 받지
              // 못했다(2026-10-07 대리 관측). 버튼이 꺼져 있을 때(빈 칸, 보내는 중)는 아무것도 하지 않는다.
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || busy || gameId.trim() === '') return
                event.preventDefault()
                void join()
              }}
              onChange={(event) => {
                setError('')
                setGameId(event.target.value)
              }}
            />
            <button
              type="button"
              data-testid="game-id-submit"
              disabled={busy || gameId.trim() === ''}
              className="btn btn-primary mt-group w-full"
              onClick={() => void join()}
            >
              {t.gameIdSubmit}
            </button>
          </section>
        )}

        {step === 'joined' && (
          <section className="mt-section" aria-label={t.joinedTitle} data-testid="joined">
            <p className="text-sm text-ink-dim">{t.joinedTitle}</p>
            <p data-testid="joined-nickname" className="mt-line text-2xl font-semibold">
              {nickname}
            </p>
            {/* 침묵하지 않는다. 빈 화면은 미구현인지 고장인지 구별되지 않는다. */}
            {/* **CT 가 수비를 보고 있으면 그렇다고 말한다** (T2 개정 둘째, 2026-10-01 사용자).
                열람 화면은 CT 가 지금 연 탭으로 먼저 서므로, 고정 문장 하나로 「rally 목록이
                뜬다」를 약속하면 그때 이 카드가 거짓말을 한다. 값은 구독할 때 받은 room 상태다.
                CT 가 그 뒤에 탭을 옮기면 낡을 수 있고, 그 자리는 수비 화면의 attack 안내 줄이
                받는다. */}
            <p data-testid="joined-body" className="mt-group text-sm text-ink-mute">
              {live.room?.operation === 'defense' ? t.joinedBodyDefense : t.joinedBody}
            </p>

            {/* **작전 열람으로 가는 길.** 화면을 세워 두고 길을 안 내면 주소를 직접 치는
                사람만 닿고, 연맹원은 주소를 모른다 — 길이 있는데 화면이 말하지 않으면 없는
                것과 같다. 자동으로 넘기지 않는 이유는 이 화면의 접속 명단이 「누가 들어와
                있나」를 보는 유일한 자리이고, 그것을 볼지 말지는 사람이 정하기 때문이다. */}
            <a
              data-testid="go-view"
              href="/view"
              className="btn btn-primary mt-block w-full"
            >
              {t.goView}
            </a>

            <PresenceList entries={live.entries} connected={live.connected} lang={lang} />

            <button
              type="button"
              data-testid="leave"
              className="btn btn-secondary mt-section w-full"
              onClick={leave}
            >
              {t.leave}
            </button>
          </section>
        )}

        {step === 'ct' && (
          <section className="mt-section" aria-label={t.ctCodeLabel} data-testid="ct-branch-panel">
            <label className="section-title caps block" htmlFor="admin-code">
              {t.ctCodeLabel}
            </label>
            <input
              id="admin-code"
              data-testid="admin-code-input"
              className="field mt-line font-mono text-base font-medium tracking-widest"
              placeholder={t.ctCodePlaceholder}
              value={adminCode}
              autoComplete="off"
              enterKeyHint="go"
              // Enter 와 폰 키보드의 이동 키는 아래 버튼과 같다. 없으면 그 키로 넘어가려는 사람이 아무 반응을 받지
              // 못했다(2026-10-07 대리 관측). 버튼이 꺼져 있을 때(빈 칸, 보내는 중)는 아무것도 하지 않는다.
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || busy || adminCode.trim() === '') return
                event.preventDefault()
                void claimCt()
              }}
              onChange={(event) => {
                setError('')
                setAdminCode(event.target.value)
              }}
            />
            <button
              type="button"
              data-testid="admin-code-submit"
              disabled={busy || adminCode.trim() === ''}
              className="btn btn-primary mt-group w-full"
              onClick={() => void claimCt()}
            >
              {t.ctSubmit}
            </button>
            <button
              type="button"
              className="btn btn-ghost mt-block w-full"
              onClick={() => {
                setStep('code')
                setError('')
              }}
            >
              {t.ctBack}
            </button>
          </section>
        )}
      </main>
    </>
  )
}

export interface PresenceListProps {
  entries: PresenceEntry[]
  connected: boolean
  lang: Lang
  /**
   * 줄에 `data-game-id` 를 실을지. 기본은 싣는다. **열람 화면은 끈다** (2026-09-17 세션 선택).
   * 그 속성은 추방이 쓰는 열쇠라 CT 화면의 일이고, 연맹원이 오래 머무는 화면에 남의 게임 ID 를
   * 실어 둘 이유가 없다.
   */
  withGameId?: boolean
  /** 위쪽 바깥 여백을 뺀다. 열람 화면의 펼친 패널처럼 테두리 안에 들어갈 때 쓴다. */
  flush?: boolean
}

/**
 * 접속 명단. **게임 ID 당 한 줄이고 그 줄에 접속 수를 병기한다.**
 *
 * 연결이 끊긴 동안 옛 명단을 그대로 두지 않고 그 사실을 함께 적는다 — 명단이 CT 의
 * 작전 판단 입력이라, 낡은 명단을 지금 명단처럼 보여 주는 것이 비어 있는 것보다 나쁘다.
 */
export function PresenceList(props: PresenceListProps): React.ReactElement {
  const t = entryStrings(props.lang)
  return (
    <div className={props.flush === true ? 'pt-group' : 'mt-section'} data-testid="presence">
      <div className="flex items-baseline justify-between">
        <h2 className="section-title caps flex items-center gap-2">
          <Icon name="users" className="text-ink-mute" />
          {t.presenceTitle}
        </h2>
        {!props.connected && (
          <span data-testid="presence-stale" className="text-xs text-ink-mute">
            {t.presenceStale}
          </span>
        )}
      </div>
      {props.entries.length === 0 ? (
        <p className="mt-group text-sm text-ink-mute">{t.presenceEmpty}</p>
      ) : (
        <ul className="mt-group divide-y divide-line-hair">
          {props.entries.map((row) => (
            <li
              key={row.gameId}
              data-testid="presence-row"
              data-game-id={props.withGameId === false ? undefined : row.gameId}
              data-connections={String(row.connections)}
              className="flex items-center justify-between py-line text-sm"
            >
              <span className="font-medium">{row.nickname}</span>
              <span className="font-mono tabular-nums text-ink-dim">
                {t.connections} {row.connections}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
